import dayjs from "dayjs";
import type { ScheduledTime } from "../types/client-education-detail.types";

/**
 * Backend'in döndürdüğü ham scheduledTime girişi.
 *
 * Sanal sınıf / eğitim planlaması "haftalık tekrar" modeliyle saklanır:
 *   { day: 0-6 (0=Pazar … 6=Cumartesi, JS Date.getDay() ile aynı), startTime, endTime }
 *
 * Oysa kursiyer tarafındaki tüm kod (resolve-cta, classroom-availability,
 * ClassSchedule, ics) somut tarihli `{ date: "YYYY-MM-DD" }` bekler. Bu uyuşmazlık
 * yüzünden haftalık saat girilen sınıflarda `date` undefined kalıp "Canlı oturum
 * henüz planlanmadı" görünüyordu. `expandScheduledTimes` haftalık girişleri
 * sınıfın tarih aralığına yayarak somut tarihli oturumlara çevirir.
 */
export interface RawScheduledTime {
  /** Haftanın günü (0=Pazar … 6=Cumartesi). Haftalık tekrar girişleri için. */
  day?: number;
  /** Somut tarih (YYYY-MM-DD). Zaten genişletilmiş / legacy girişler için. */
  date?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
}

/** Güvenlik sınırı: tek seferde en fazla ~1 yıllık aralık taranır. */
const MAX_RANGE_DAYS = 370;

/**
 * Haftalık `{ day }` girişlerini [startDate, endDate] aralığındaki somut tarihlere
 * yayar. Zaten `date` taşıyan girişler olduğu gibi korunur (legacy/test uyumu).
 * Aralık verilmemiş ya da geçersizse haftalık girişler genişletilemez ve atlanır.
 */
export function expandScheduledTimes(
  times: RawScheduledTime[] | null | undefined,
  startDate?: string | Date | null,
  endDate?: string | Date | null,
): ScheduledTime[] {
  const list = times ?? [];
  if (list.length === 0) return [];

  const out: ScheduledTime[] = [];
  const weekly: RawScheduledTime[] = [];

  for (const t of list) {
    if (t.date) {
      out.push({ date: t.date, startTime: t.startTime, endTime: t.endTime });
    } else if (typeof t.day === "number") {
      weekly.push(t);
    }
  }

  if (weekly.length > 0) {
    const start = startDate ? dayjs(startDate).startOf("day") : null;
    const end = endDate ? dayjs(endDate).startOf("day") : null;
    if (start?.isValid() && end?.isValid() && !end.isBefore(start)) {
      const byDay = new Map<number, RawScheduledTime[]>();
      for (const w of weekly) {
        const arr = byDay.get(w.day as number) ?? [];
        arr.push(w);
        byDay.set(w.day as number, arr);
      }
      let cursor = start;
      let guard = 0;
      while (!cursor.isAfter(end) && guard < MAX_RANGE_DAYS) {
        const matches = byDay.get(cursor.day()); // dayjs .day(): 0=Pazar
        if (matches) {
          const dateStr = cursor.format("YYYY-MM-DD");
          for (const m of matches) {
            out.push({ date: dateStr, startTime: m.startTime, endTime: m.endTime });
          }
        }
        cursor = cursor.add(1, "day");
        guard++;
      }
    }
  }

  out.sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  return out;
}

/**
 * Verilen tarih aralığında (uçlar dahil) görülen haftanın günleri (0=Pazar … 6=Cmt).
 * Manager tarafında "seçilen haftalık gün aralığa denk geliyor mu" kontrolü için.
 */
export function weekdaysInRange(
  startDate?: string | Date | null,
  endDate?: string | Date | null,
): Set<number> {
  const set = new Set<number>();
  const start = startDate ? dayjs(startDate).startOf("day") : null;
  const end = endDate ? dayjs(endDate).startOf("day") : null;
  if (!start?.isValid() || !end?.isValid() || end.isBefore(start)) return set;

  let cursor = start;
  let guard = 0;
  while (!cursor.isAfter(end) && guard < MAX_RANGE_DAYS) {
    set.add(cursor.day());
    if (set.size === 7) break;
    cursor = cursor.add(1, "day");
    guard++;
  }
  return set;
}
